import baseApi from './api';

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  role?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface Role {
  id: string;
  name: string;
  description?: string;
}

export interface ClinicUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: 'invited' | 'active' | 'inactive' | 'archived';
  is_active: boolean;
  user_type: string;
  role: Role | null;
  created_at: string;
  updated_at: string;
}

export interface CreateClinicUserRequest {
  first_name: string;
  last_name?: string;
  email: string;
  role_id: string;
  phone_number?: string;
}

export interface UpdateClinicUserRequest {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  role_id?: string;
}

export interface ClinicUsersResponse {
  success: boolean;
  data: {
    users: ClinicUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface ClinicUserResponse {
  success: boolean;
  data: {
    user: ClinicUser;
  };
  message?: string;
}

export const clinicUserService = {
  /**
   * Get all users for a clinic
   */
  getClinicUsers: async (
    clinicId: string,
    params?: PaginationParams
  ): Promise<ClinicUsersResponse> => {
    const response = await baseApi.get(`/clinic/${clinicId}/users`, { params });
    return response.data;
  },

  /**
   * Create a new clinic user
   */
  createClinicUser: async (
    clinicId: string,
    data: CreateClinicUserRequest
  ): Promise<ClinicUserResponse> => {
    const response = await baseApi.post(`/clinic/${clinicId}/users`, data);
    return response.data;
  },

  /**
   * Get user by ID
   */
  getClinicUserById: async (clinicId: string, userId: string): Promise<ClinicUserResponse> => {
    const response = await baseApi.get(`/clinic/${clinicId}/users/${userId}`);
    return response.data;
  },

  /**
   * Update user
   */
  updateClinicUser: async (
    clinicId: string,
    userId: string,
    data: UpdateClinicUserRequest
  ): Promise<ClinicUserResponse> => {
    const response = await baseApi.put(`/clinic/${clinicId}/users/${userId}`, data);
    return response.data;
  },

  /**
   * Toggle user status (active/inactive)
   */
  toggleUserStatus: async (clinicId: string, userId: string): Promise<ClinicUserResponse> => {
    const response = await baseApi.patch(`/clinic/${clinicId}/users/${userId}/status`);
    return response.data;
  },

  /**
   * Send/resend invitation to user
   */
  inviteUser: async (
    clinicId: string,
    userId: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await baseApi.post(`/clinic/${clinicId}/users/${userId}/invite`);
    return response.data;
  },
};

